/**
 * @file challenges.js
 * @description Functions for working with challenges.
 */

import { doc, getDoc, setDoc, writeBatch } from "firebase/firestore";
import { getCurrentLocalDate, getValidDays } from "../../utils/dateUtils";
import { bookNameToChapterCounts, listOfBooks } from "../../constants";
import { createTemporaryUserFromEmail, getUIDfromEmail } from "../../firebase";

/**
 * Check if a given user (UID) is in a given challenge.
 * @param db {firebase.firestore.Firestore}
 * @param uid {string}
 * @param challengeID {string}
 * @returns {Promise<boolean>}
 */
export async function checkIfUserIsInChallenge(db, uid, challengeID) {
  const userChallengeDoc = doc(
    db,
    "users",
    uid,
    "challenges",
    challengeID
  );
  const userChallengeDocSnapshot = await getDoc(userChallengeDoc);
  return !!userChallengeDocSnapshot.exists();
}

/**
 * Add a user (UID) to a challenge.
 * @param db {Firestore}
 * @param uid {string}
 * @param challengeID {string}
 * @returns {Promise<void>}
 */
export async function addUserToChallenge(db, uid, challengeID, options = {}) {
  // Get challenge document to determine reading portions
  const challengeDocSnap = await getDoc(doc(db, "challenges", challengeID));
  if (!challengeDocSnap.exists()) {
    console.log(
      "Tried adding a user to a challenge where the challenge doesn't exist."
    );
    return;
  }
  
  // Use batch write to atomically add the challenge to the user's list
  // and the user to the challenge's list of users
  const batch = writeBatch(db);
  const userChallengeDocRef = doc(db, "users", uid, "challenges", challengeID);
  const challengeUserList = doc(db, "challenges", challengeID, "users", uid);
  
  const emailUpdates =
    Object.prototype.hasOwnProperty.call(options, "emailUpdates")
      ? options.emailUpdates
      : true;
  batch.set(userChallengeDocRef, {
    progress: "0".repeat(challengeDocSnap.data().reading_portions),
    emailUpdates,
  });
  batch.set(challengeUserList, {});
  
  await batch.commit();
}

/**
 * Remove a user (UID) from a challenge.
 * @param db {Firestore}
 * @param uid {string}
 * @param challengeID {string}
 * @returns {Promise<void>}
 */
export async function removeUserFromChallenge(db, uid, challengeID) {
  // Use batch write to atomically remove the challenge from the user's list
  // and the user from the challenge's list of users
  const batch = writeBatch(db);
  const userChallengeInfo = doc(db, "users", uid, "challenges", challengeID);
  const challengeUserList = doc(db, "challenges", challengeID, "users", uid);
  
  batch.delete(userChallengeInfo);
  batch.delete(challengeUserList);
  
  await batch.commit();
}

/**
 * Get the total chapters in a list of reading portions
 * @param readingPortions {[string, int][][]}
 * @return {number}
 */
export function getTotalChapters(readingPortions) {
  let totalChapters = 0;

  for (const [startBookAndChapter, endBookAndChapter] of readingPortions) {
    let currentBook = startBookAndChapter[0];
    let currentChapter = startBookAndChapter[1];

    // Count the first chapter
    totalChapters++;

    while (
      currentBook !== endBookAndChapter[0] ||
      currentChapter !== endBookAndChapter[1]
    ) {
      currentChapter++;

      // Check if we need to move to the next book
      if (currentChapter > bookNameToChapterCounts[currentBook]) {
        const currentBookIndex = listOfBooks.indexOf(currentBook);
        if (currentBookIndex >= 0 && currentBookIndex < listOfBooks.length - 1) {
          currentBook = listOfBooks[currentBookIndex + 1];
          currentChapter = 1;
        } else {
          // We've reached the end of the Bible, break out of the loop
          break;
        }
      }

      totalChapters++;
    }
  }

  return totalChapters;
}

/**
 * Get the reading schedule based on the inputs of a challenge.
 * @param startDate {string} The start date of the challenge (inclusive) in YYYY-MM-DD format
 * @param endDate {string} The end date of the challenge (inclusive) in YYYY-MM-DD format - if empty, will generate enough days for all chapters
 * @param daysOfTheWeek {string[]} Array of day names (e.g., ['Monday', 'Wednesday', 'Friday'])
 * @param readingPortions {[string, int][][]} Reading portions from getReadingPortionsFromBooks()
 * @param datesToSkip {Date[]|string[]} Array of dates to skip during the challenge (can be Date objects or strings)
 * @returns {[Date, [string, number][]][]} Array of pairs. Each pair is a Date object in local timezone and an array of chapters to read on that date.
 */
export function getDatesToChapters(
  startDate,
  endDate,
  daysOfTheWeek,
  readingPortions,
  datesToSkip
) {
  // Handle empty reading portions
  if (!readingPortions || readingPortions.length === 0) {
    return [];
  }

  // Get valid days as Date objects in local timezone
  let validDays = getValidDays(startDate, endDate, daysOfTheWeek, datesToSkip);
  const totalChapters = getTotalChapters(readingPortions);
  
  // if end date was empty, then adjust validDays to be the correct length
  // This ensures 1 chapter per day when no end date is provided
  if (endDate === "") {
    validDays = validDays.slice(0, totalChapters);
  }
  
  const chaptersPerDay = endDate === "" ? 1 : totalChapters / validDays.length;

  let currentPortionIndex = 0;
  let [currentBook, currentChapter] = readingPortions[currentPortionIndex][0];
  const schedule = [];
  let chaptersRead = 0;
  let chaptersShouldHaveRead = 0;

  for (const validDay of validDays) {
    const chaptersToRead = [];
    // calculate how many chapters should be read on this day
    chaptersShouldHaveRead += chaptersPerDay;
    let chaptersToReadOnThisDay =
      Math.round(chaptersShouldHaveRead) - chaptersRead;
    
    for (let i = 0; i < chaptersToReadOnThisDay; i++) {
      // Break if we've read all chapters
      if (chaptersRead >= totalChapters) {
        break;
      }
      
      chaptersToRead.push([currentBook, currentChapter]);
      
      // Check if we've reached the end of the current reading portion
      const [currentEndBook, currentEndChapter] =
        readingPortions[currentPortionIndex][1];
      if (
        currentBook === currentEndBook &&
        currentChapter === currentEndChapter
      ) {
        // Move to the next reading portion
        currentPortionIndex++;
        if (currentPortionIndex < readingPortions.length) {
          [currentBook, currentChapter] =
            readingPortions[currentPortionIndex][0];
        } else {
          // We've finished all reading portions
          break;
        }
      } else {
        // Move to the next chapter
        currentChapter++;
        
        // Check if we need to move to the next book within the current portion
        if (currentChapter > bookNameToChapterCounts[currentBook]) {
          const currentBookIndex = listOfBooks.indexOf(currentBook);
          if (currentBookIndex >= 0 && currentBookIndex < listOfBooks.length - 1) {
            currentBook = listOfBooks[currentBookIndex + 1];
            currentChapter = 1;
          }
        }
      }
    }
    chaptersRead += chaptersToRead.length;
    schedule.push([validDay, chaptersToRead]);
    
    // For no end date scenarios, break after scheduling all chapters
    if (endDate === "" && chaptersRead >= totalChapters) {
      break;
    }
  }

  return schedule;
}

/**
 * Update get-emails property of user in a challenge.
 * @param db {firebase.firestore.Firestore}
 * @param uid {string}
 * @param challengeID {string}
 * @param getEmails {boolean}
 */
export async function updateGetEmails(db, uid, challengeID, getEmails) {
  if (db === null) return;
  if (uid === null) return;
  if (challengeID === null) return;
  const userChallengeInfo = doc(db, "users", uid, "challenges", challengeID);
  const oldDoc = await getDoc(userChallengeInfo);
  if (!oldDoc.exists()) {
    console.log("Tried updating email preferences for a challenge the user is not in.");
    return;
  }
  // Use merge to prevent race conditions and preserve other fields
  await setDoc(userChallengeInfo, {
    emailUpdates: getEmails,
    progress: oldDoc.data().progress,
  }, { merge: true });
}

/**
 * Mark a user's reading as complete/incomplete for a given reading portion.
 * @param db {Firestore}
 * @param uid {string}
 * @param challengeID {string}
 * @param readingPortionIndex {int} Must be 1 indexed!!
 * @param complete {boolean}
 * @returns {Promise<boolean>}
 */
export async function markReadingPortion(
  db,
  uid,
  challengeID,
  readingPortionIndex,
  complete
) {
  if (readingPortionIndex == null || readingPortionIndex === 0 || uid == null) {
    return false;
  }
  // update the challenge progress in the database
  const userChallengeDocRef = doc(
    db,
    "users",
    uid,
    "challenges",
    challengeID
  );
  const userChallengeDoc = await getDoc(userChallengeDocRef);
  if (!userChallengeDoc.exists()) {
    return false;
  }
  const userChallengeInfo = userChallengeDoc.data();
  const newChallengeProgress =
    userChallengeInfo.progress.substring(0, readingPortionIndex - 1) +
    (complete ? "1" : "0") +
    userChallengeInfo.progress.substring(readingPortionIndex);
  // Use merge to prevent race conditions and preserve other fields
  await setDoc(userChallengeDocRef, {
    progress: newChallengeProgress,
    emailUpdates: userChallengeInfo.emailUpdates,
    lastUpdated: Date.now(),
  }, { merge: true });
  return true;
}

/**
 * Get last read date based on the progress and the schedule.
 * @param progress {string}
 * @param datesToChapters {[[Date, [string, int][]]]}
 */
export function getLastReadDate(progress, datesToChapters) {
  let lastReadDate = null;
  for (let i = 0; i < progress.length; i++) {
    if (progress[i] === "1") {
      lastReadDate = datesToChapters[i][0];
    }
  }
  return lastReadDate;
}

/**
 * Get percentage of challenge completed based on the progress and the schedule.
 * @param progress {string}
 * @param datesToChapters {[[Date, [string, int][]]]}
 */
export function getPercentageCompleted(progress, datesToChapters) {
  let chaptersRead = 0;
  for (let i = 0; i < progress.length; i++) {
    if (progress[i] === "1") {
      chaptersRead += 1;
    }
  }
  return (chaptersRead / datesToChapters.length) * 100;
}

/**
 * Get the most recent portion index that should be read. Returns 1 indexed.
 * @param datesToChapters {[[Date, [string, int][]]]}
 */
export function getMostRecentPortionIndex(datesToChapters) {
  let readingPortionClosest = null;
  for (let i = 0; i < datesToChapters.length; i++) {
    let isBeforeCurrentTime = datesToChapters[i][0] < getCurrentLocalDate();
    if (isBeforeCurrentTime) {
      readingPortionClosest = i + 1;
    }
  }
  return readingPortionClosest;
}

/**
 * Get the first unread portion index that should be read. Returns 1 indexed.
 * @param challengeProgress {string} e.g. "00001101"
 */
export function getFirstUnreadPortionIndex(challengeProgress) {
  let firstUnreadPortionIndex = null;
  for (let i = 0; i < challengeProgress.length; i++) {
    if (challengeProgress[i] === "0") {
      firstUnreadPortionIndex = i + 1;
      break;
    }
  }
  return firstUnreadPortionIndex;
}

/**
 * Add a participant to a challenge using their email.
 * @param db {Firestore}
 * @param challengeID {string}
 * @param email {string}
 * @returns {Promise<[boolean,string]>} [success, message]
 */
export async function addUserToChallengeByEmail(db, challengeID, email) {
  // validate email using regex
  const regex =
    "^([\\w-]+(?:\\.[\\w-]+)*)@((?:[\\w-]+\\.)*\\w[\\w-]{0,66})\\.([a-z]{2,6}(?:\\.[a-z]{2})?)$";
  if (!email.match(regex)) {
    return [false, "Invalid email address."];
  }
  // get user id from email
  const uid = await getUIDfromEmail(db, email);
  if (uid) {
    // check if the user is already in the challenge
    const inChallenge = await checkIfUserIsInChallenge(db, uid, challengeID);
    if (inChallenge) {
      return [false, "This user is already in the challenge."];
    } else {
      await addUserToChallenge(db, uid, challengeID, { emailUpdates: false });
      return [true, "User added to challenge."];
    }
  } else {
    // create a temporary user
    let tempUID = await createTemporaryUserFromEmail(db, email);
    // add the user to the challenge
    await addUserToChallenge(db, tempUID, challengeID, { emailUpdates: false });
    return [true, "User added to challenge."];
  }
}
